import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { useProductFilterStore } from '#client/stores/productFilter/useProductFilterStore';

// Initial state for the cycle count store
const initialCycleCountState = {
    // Cycle Count Mode State
    mode: 'view',

    // Form State
    selectedStoreIds: [],
    storeSearchValue: '',
    countType: '',
    reasonCode: '',
    description: '',
    isSubmitting: false,
    showValidationErrors: false,

    // Toast State
    toastError: '',
    success: '',
    warning: '',
    errors: {},
};

// Create the cycle count store with combine middleware for better type inference
const useCycleCountStoreBase = create(
    combine(initialCycleCountState, (set) => ({
        // Actions for Cycle Count Mode
        setMode: (mode) => {
            set({ mode });
            // Also set mode in product filter store
            useProductFilterStore.getState().setMode(mode);
        },

        // Actions for Form State
        setSelectedStoreIds: (selectedStoreIds) => set({ selectedStoreIds }),
        setStoreSearchValue: (storeSearchValue) => set({ storeSearchValue }),
        setCountType: (countType) => set({ countType }),
        setReasonCode: (reasonCode) => set({ reasonCode }),
        setDescription: (description) => set({ description }),
        setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
        setShowValidationErrors: (showValidationErrors) => set({ showValidationErrors }),

        // Actions for Toast State
        setToastError: (toastError) => set({ toastError }),
        setSuccess: (success) => set({ success }),
        setWarning: (warning) => set({ warning }),
        setErrors: (errors) => set({ errors }),

        // Reset cycle count form
        resetCycleCount: () => {
            set({
                ...initialCycleCountState,
            });
            // Also reset product filter
            useProductFilterStore.getState().resetProductFilter();
        },
    }))
);

// Auto-generated selectors function
const createSelectors = (_store) => {
    let store = _store;
    store.use = {};

    for (let k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }

    return store;
};

// Create the store with auto-generated selectors
export const useCycleCountStore = createSelectors(useCycleCountStoreBase);

/**
 * Selector hooks for optimized component rendering
 * Each hook uses useShallow to prevent unnecessary re-renders
 */

// Selector for cycle count actions
export const useCycleCountActions = () =>
    useCycleCountStore(
        useShallow((state) => ({
            setMode: state.setMode,
            setSelectedStoreIds: state.setSelectedStoreIds,
            setStoreSearchValue: state.setStoreSearchValue,
            setCountType: state.setCountType,
            setReasonCode: state.setReasonCode,
            setDescription: state.setDescription,
            setIsSubmitting: state.setIsSubmitting,
            setShowValidationErrors: state.setShowValidationErrors,
            setToastError: state.setToastError,
            setSuccess: state.setSuccess,
            setWarning: state.setWarning,
            setErrors: state.setErrors,
            resetCycleCount: state.resetCycleCount,
        }))
    );

// Selector for cycle count form state
export const useCycleCountForm = () =>
    useCycleCountStore(
        useShallow((state) => ({
            mode: state.mode,
            selectedStoreIds: state.selectedStoreIds,
            storeSearchValue: state.storeSearchValue,
            countType: state.countType,
            reasonCode: state.reasonCode,
            description: state.description,
            isSubmitting: state.isSubmitting,
            showValidationErrors: state.showValidationErrors,
        }))
    );

// Selector for cycle count toast messages
export const useCycleCountToast = () =>
    useCycleCountStore(
        useShallow((state) => ({
            toastError: state.toastError,
            success: state.success,
            warning: state.warning,
            errors: state.errors,
            setToastError: state.setToastError,
        }))
    );

// Re-export product filter hooks for convenience
export { useProductFilterStore, useProductFilterActions, useMultiProductFilter } from '#client/stores/productFilter/useProductFilterStore';
